"use client";

import React from "react";
import { FieldValues, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SimpleFieldConfig } from "../../UniversalCRMTable/types";
import { FieldHelpPopover } from "@/components/shared/FieldHelp/FieldHelpPopover";

/**
 * Provides the entity key (e.g. "deals") to every FieldWrapper in a create/edit
 * form so each field label can render its "?" help popover via getFieldHelp,
 * without threading `entity` through every field renderer. Mounted once at the
 * form root; FieldWrapper consumes it. Empty string = no entity (help hidden).
 */
const FormFieldEntityContext = React.createContext<string>("");

export function FormFieldEntityProvider({
  entity,
  children,
}: {
  entity?: string;
  children: React.ReactNode;
}) {
  return (
    <FormFieldEntityContext.Provider value={entity ?? ""}>
      {children}
    </FormFieldEntityContext.Provider>
  );
}

export function useFormFieldEntity(): string {
  return React.useContext(FormFieldEntityContext);
}

/**
 * Context passed to field renderers.
 * Contains all the form state and utilities needed to render a field.
 */
export interface FieldRenderContext {
  fieldName: string;
  field: SimpleFieldConfig;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watchedValues: Record<string, any>;
  errors: Record<string, any>;
  config: any; // SimpleTableConfig
  customComponents?: Record<string, React.ComponentType<any>>;
  appUserId?: string;
  clientOptions?: any[];
  clientsLoading?: boolean;
  isSearching?: boolean;
  searchValue?: string;
  setSearchValue?: (value: string) => void;

  /** True when the signed-in user is a client_user. */
  isClientUser?: boolean;
  /**
   * The client's own contact id (their single direct grant), when resolvable.
   * Drives the "Assign to me" quick action + auto-fill on client-select fields.
   */
  clientSelfContactId?: string;
  /** Display name of the client's own contact, for the "Assign to me" label. */
  clientSelfContactName?: string;

  /** For expandInline selects: "replace" (default) or "floating" */
  expandInlineVariant?: "replace" | "floating";
}

/**
 * Shared wrapper component for form fields.
 * Handles label, required indicator, error styling, and error message.
 */
export interface FieldWrapperProps {
  fieldName: string;
  field: SimpleFieldConfig;
  hasError: boolean;
  errorMessage?: string;
  children: React.ReactNode;
  /** Override the label (e.g. for live-updating values like "Probability 50%") */
  labelOverride?: React.ReactNode;
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  fieldName,
  field,
  hasError,
  errorMessage,
  children,
  labelOverride,
}) => {
  const entity = useFormFieldEntity();
  return (
    <div key={fieldName} className={cn("space-y-2", hasError && "animate-shake")}>
      <Label
        htmlFor={fieldName}
        className={cn("flex items-center gap-1.5", hasError && "text-red-700 font-semibold")}
      >
        <span>
          {labelOverride ?? field.label} {field.required && <span className="text-red-500">*</span>}
        </span>
        {entity ? <FieldHelpPopover entity={entity} fieldId={fieldName} side="right" align="start" /> : null}
      </Label>
      {children}
      {hasError && (
        <p
          id={`${fieldName}-error`}
          className="text-sm text-red-600 font-medium flex items-start gap-1"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{errorMessage}</span>
        </p>
      )}
    </div>
  );
};
